'use client'

// The Alkanes view of /tokens.
//
// Alkanes trade on the AMM, not the marketplace orderbook: the orderbook feed
// the other protocols use has no listings, no volume and no floor for them,
// so every column it could show is a zero. This table reads the same
// per-asset AMM feed the /alkanes terminal does, sorts and filters on AMM
// metrics, and every row opens that token's trading page in the terminal.

import React from 'react'
import Link from 'next/link'
import useSWR from 'swr'
import { ArrowDown, ArrowUpRight, Search } from 'lucide-react'
import { API_URL } from '@/utils/constants'
import { formatUsdPrice, usdCompact } from '@/app/alkanes/lib/format'
import type { AssetRow } from '@/app/alkanes/lib/useAssets'

export const ALKANES_AMM_SORTS = {
	volume_24h_usd: '24H volume',
	market_cap_usd: 'Market cap',
	liquidity_usd: 'Liquidity',
	change_24h_pct: '24H change',
	holders: 'Holders'
} as const
const SORTS = ALKANES_AMM_SORTS
type AmmSort = keyof typeof SORTS

const gradient = 'bg-gradient-to-r from-purple-600 to-blue-500 text-white'

function Change({ v }: { v: number | null | undefined }) {
	if (v === null || v === undefined || !Number.isFinite(v)) {
		return <span className="text-muted-foreground">—</span>
	}
	const up = v >= 0
	return (
		<span
			aria-label={`${up ? 'up' : 'down'} ${Math.abs(v).toFixed(2)} percent in 24 hours`}
			className={up ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}
		>
			<span aria-hidden="true" className="mr-0.5 text-[0.7em]">
				{up ? '▲' : '▼'}
			</span>
			{Math.abs(v).toFixed(2)}%
		</span>
	)
}

function TokenIcon({ asset }: { asset: AssetRow }) {
	const [broken, setBroken] = React.useState(false)
	if (!asset.icon || broken) {
		return (
			<span className="grid size-9 shrink-0 place-items-center rounded-lg bg-muted text-[10px] font-bold text-muted-foreground">
				{(asset.name || asset.id).slice(0, 2).toUpperCase()}
			</span>
		)
	}
	return (
		<img
			src={asset.icon}
			alt=""
			loading="lazy"
			onError={() => setBroken(true)}
			className="size-9 shrink-0 rounded-lg object-cover"
		/>
	)
}

export default function AlkanesAmmTable(props: {
	/// Omit `search` and the table renders its own search box — for host
	/// pages (/collect) that have none.
	search?: string
	sortParam: string
	onSort: (sort: string) => void
}) {
	const standalone = props.search === undefined
	const [ownSearch, setOwnSearch] = React.useState('')
	const search = standalone ? ownSearch : props.search ?? ''
	const { sortParam, onSort } = props
	const { data, error, isLoading, mutate } = useSWR<{ assets?: AssetRow[] }>(
		`${API_URL}/alkane/assets`,
		async (url: string) => {
			const response = await fetch(url)
			if (!response.ok) throw new Error('Alkanes markets unavailable')
			return response.json()
		},
		{ refreshInterval: 60_000, revalidateOnFocus: true, refreshWhenHidden: false }
	)

	const sort: AmmSort = Object.hasOwn(SORTS, sortParam) ? (sortParam as AmmSort) : 'volume_24h_usd'
	const assets = React.useMemo(() => data?.assets ?? [], [data])
	const rows = React.useMemo(() => {
		const query = search.trim().toLowerCase()
		return assets
			.filter(
				(a) => !query || (a.name || '').toLowerCase().includes(query) || a.id.toLowerCase().includes(query)
			)
			.slice()
			.sort((a, b) => {
				// A missing figure is unknown, not zero: it sorts last, never first.
				const av = a[sort]
				const bv = b[sort]
				const aKnown = typeof av === 'number' && Number.isFinite(av)
				const bKnown = typeof bv === 'number' && Number.isFinite(bv)
				if (!aKnown || !bKnown) {
					if (aKnown) return -1
					if (bKnown) return 1
					return b.liquidity_usd - a.liquidity_usd
				}
				return bv - av || b.liquidity_usd - a.liquidity_usd
			})
	}, [assets, search, sort])

	const totalVolume = assets.reduce((n, a) => n + (a.volume_24h_usd ?? 0), 0)
	// Pool liquidity is two-sided: summing per-asset depth would count every
	// pool twice, so the headline is the deepest single market instead.
	const deepest = assets.reduce<AssetRow | null>(
		(best, a) => (a.id !== '32:0' && (!best || a.liquidity_usd > best.liquidity_usd) ? a : best),
		null
	)

	return (
		<>
			<div className="hidden gap-3 border-b border-border p-4 sm:grid sm:grid-cols-3 sm:p-5">
				{[
					['Alkanes with a live pool', isLoading ? '—' : assets.length.toLocaleString()],
					['24H AMM volume', isLoading ? '—' : usdCompact(totalVolume)],
					[
						'Deepest market',
						isLoading || !deepest
							? '—'
							: `${deepest.name || deepest.id} · ${usdCompact(deepest.liquidity_usd)}`
					]
				].map(([label, value]) => (
					<div key={label} className="rounded-xl border border-border bg-card/40 px-5 py-4">
						<p className="block text-xs text-muted-foreground">{label}</p>
						<p className="mt-2 block truncate text-xl font-semibold tabular-nums">{value}</p>
					</div>
				))}
			</div>
			<div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-5 py-3">
				{standalone ? (
					<label className="relative min-w-0 flex-1 sm:max-w-sm">
						<span className="sr-only">Search alkanes</span>
						<Search className="absolute left-3 top-3 size-4 text-muted-foreground" aria-hidden="true" />
						<input
							type="search"
							value={ownSearch}
							onChange={(e) => setOwnSearch(e.target.value)}
							placeholder="Search by name or alkane id"
							className="h-10 w-full rounded-lg border border-border bg-background pl-10 pr-3 text-base outline-none focus-visible:ring-2 focus-visible:ring-ring sm:text-sm"
						/>
					</label>
				) : (
					<p className="text-xs text-muted-foreground">
						Alkanes trade on the AMM. Prices, volume and liquidity come from live pools, in USD.
					</p>
				)}
				<Link
					href="/alkanes"
					className={`inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-medium ${gradient}`}
				>
					Open Alkanes trading
					<ArrowUpRight className="size-3.5" aria-hidden="true" />
				</Link>
			</div>
			<div className="overflow-x-auto">
				<table className="w-full min-w-[860px] text-left text-sm" data-testid="tokens-alkanes-amm">
					<caption className="sr-only">Alkanes AMM markets sorted by {SORTS[sort]}, highest first</caption>
					<thead className="border-b border-border bg-muted/40 text-[10px] text-muted-foreground">
						<tr>
							<th scope="col" className="px-5 py-3">
								Token
							</th>
							<th scope="col" className="px-3 py-3 text-right">
								Price
							</th>
							{(Object.keys(SORTS) as AmmSort[]).map((key) => (
								<th
									key={key}
									scope="col"
									className="px-3 py-3 text-right"
									aria-sort={sort === key ? 'descending' : 'none'}
								>
									<button
										type="button"
										onClick={() => onSort(key)}
										className="inline-flex items-center gap-1 whitespace-nowrap"
									>
										{SORTS[key]}
										{sort === key ? <ArrowDown className="size-3" aria-hidden="true" /> : null}
									</button>
								</th>
							))}
						</tr>
					</thead>
					<tbody className="divide-y divide-border/60">
						{rows.map((a) => (
							<tr key={a.id} className="transition-colors hover:bg-muted/40">
								<td className="px-5 py-3">
									<Link
										href={`/alkanes/${a.id}`}
										className="group flex min-w-[180px] max-w-[280px] items-center gap-3 rounded-sm focus-visible:ring-2 focus-visible:ring-ring"
									>
										<TokenIcon asset={a} />
										<span className="min-w-0 flex-1">
											<span className="block truncate font-semibold group-hover:text-purple-500">
												{a.name || a.id}
											</span>
											<span className="block text-xs tabular-nums text-muted-foreground">{a.id}</span>
										</span>
										<ArrowUpRight className="size-3 shrink-0 text-muted-foreground" aria-hidden="true" />
									</Link>
								</td>
								<td className="whitespace-nowrap px-3 py-3 text-right font-medium tabular-nums">
									{a.price_usd !== null ? formatUsdPrice(a.price_usd) : '—'}
								</td>
								<td className="whitespace-nowrap px-3 py-3 text-right text-xs tabular-nums">
									{a.volume_24h_usd !== null && a.volume_24h_usd !== undefined
										? usdCompact(a.volume_24h_usd)
										: '—'}
								</td>
								<td className="whitespace-nowrap px-3 py-3 text-right text-xs tabular-nums">
									{a.market_cap_usd ? usdCompact(a.market_cap_usd) : '—'}
								</td>
								<td className="whitespace-nowrap px-3 py-3 text-right text-xs tabular-nums">
									{usdCompact(a.liquidity_usd)}
								</td>
								<td className="whitespace-nowrap px-3 py-3 text-right text-xs tabular-nums">
									<Change v={a.change_24h_pct} />
								</td>
								<td className="whitespace-nowrap px-3 py-3 text-right text-xs tabular-nums">
									{a.holders?.toLocaleString() ?? '—'}
								</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>
			{isLoading ? (
				<p className="py-16 text-center text-sm text-muted-foreground">Loading Alkanes markets…</p>
			) : rows.length === 0 ? (
				<div className="px-5 py-16 text-center">
					<p className="font-medium">
						{error ? 'Alkanes markets are temporarily unavailable' : `No alkane matches “${search.trim()}”`}
					</p>
					<p className="mt-2 text-sm text-muted-foreground">
						{error ? 'Please retry in a moment.' : 'Search by name, or by id like 2:0.'}
					</p>
					{error && (
						<button className={`mt-4 rounded-lg px-4 py-2 text-sm ${gradient}`} onClick={() => void mutate()}>
							Retry
						</button>
					)}
				</div>
			) : (
				<p className="border-t border-border px-5 py-4 text-xs text-muted-foreground">
					{rows.length.toLocaleString()} {rows.length === 1 ? 'market' : 'markets'} · {SORTS[sort].toLowerCase()},
					highest first · updates every minute
				</p>
			)}
		</>
	)
}
